'use client'

import * as React from 'react'
import { createStore, useStore } from 'zustand'

import type {
  ComponentBlock,
  InstanceBlock,
} from '@basehub/data/replicache/index'

type RichTextStore = {
  recentlyCreatedBlock: InstanceBlock | ComponentBlock | null
  setRecentlyCreatedBlock: (
    block: InstanceBlock | ComponentBlock | null
  ) => void
}

const createRichTextStore = () =>
  createStore<RichTextStore>()((set) => ({
    recentlyCreatedBlock: null,
    setRecentlyCreatedBlock: (block) => set({ recentlyCreatedBlock: block }),
  }))

const RichTextStoreContext = React.createContext<ReturnType<
  typeof createRichTextStore
> | null>(null)
export const useRichTextStore = <T, U extends boolean = false>(
  selector: (state: RichTextStore) => T,
  permissive?: U
): U extends true ? T | undefined : T => {
  const store = React.useContext(RichTextStoreContext)
  if (!store) {
    if (permissive) {
      return undefined as U extends true ? T | undefined : T
    }
    throw new Error('Missing RichTextStoreContext.Provider in the tree')
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useStore(store, selector)
}

export const RichTextStoreProvider = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const store = React.useMemo(() => createRichTextStore(), [])

  return (
    <RichTextStoreContext.Provider value={store}>
      {children}
    </RichTextStoreContext.Provider>
  )
}
